import randomBytes from 'randombytes'
import { UAParser } from 'ua-parser-js'
import Router from 'next/router'
import * as React from 'react'
import { useSelector } from 'react-redux'
import { loggedInSelector } from '@chegg-wt/core/state/user/selectors'
import { HeaderUser, getJerichoCsrfToken, getJerichoHeaderUser } from '../api/jericho'
import { trackWithoutThrow } from '../utils/errors'

// Chegg UI will ultimately provide a hook or some utility like this
export interface CheggHeaderSession {
  serverRenderAsMobile: boolean
  user: HeaderUser | null
  csrfToken: string
  pageTrackId: string
  error: boolean
  completed: boolean
}

interface TokenAndUser {
  user: HeaderUser | null
  csrfToken: string
}

export interface CheggHeaderSessionProps {
  userAgent?: string
  skip?: boolean
}

const getUserAndToken = async (): Promise<TokenAndUser> => {
  const csrfToken = await getJerichoCsrfToken()
  const user = await getJerichoHeaderUser(csrfToken)
  return Promise.resolve({ csrfToken, user })
}

const isMobileOrTabletUA = (userAgent: string): boolean => {
  // if for some reason user agent does not exist, treat as mobile for header largest contentful paint optimisation
  if (!userAgent) {
    return true
  }
  // used to determine initial "guess" for SSR: https://github.com/ReactTraining/react-media#server-side-rendering-ssr
  const userDeviceType = new UAParser(userAgent).getDevice().type || ''
  return ['mobile', 'wearable', 'embedded', 'tablet'].includes(userDeviceType)
}

const useCheggHeaderSession = ({
  userAgent,
  skip,
}: CheggHeaderSessionProps = {}): CheggHeaderSession => {
  const [csrfToken, setCsrfToken] = React.useState<string>('')
  const [user, setUser] = React.useState<HeaderUser | null>(null)
  const [completed, setCompleted] = React.useState<boolean>(false)

  const [pageTrackId, setPageTrackId] = React.useState<string>('')
  const [error, setError] = React.useState<boolean>(false)

  const isLoggedIn = useSelector(loggedInSelector)

  React.useEffect(() => {
    if (!skip) {
      getUserAndToken()
        .then(({ csrfToken, user }) => {
          setCsrfToken(csrfToken)
          setUser(user)
          setCompleted(true)
          setError(false)
        })
        .catch((e) => {
          trackWithoutThrow(e)
          setCompleted(true)
          setError(true)
        })
    }
  }, [skip, isLoggedIn])

  const handleRouteChange = () => {
    setPageTrackId(randomBytes(16).toString('hex'))
  }

  React.useEffect(() => {
    handleRouteChange()
    Router.events.on('routeChangeComplete', handleRouteChange)
    return () => Router.events.off('routeChangeComplete', handleRouteChange)
  }, [])

  const serverRenderAsMobile = isMobileOrTabletUA(userAgent)

  return { serverRenderAsMobile, csrfToken, user, pageTrackId, error, completed }
}

export default useCheggHeaderSession
